"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Flame, PhoneCall, ShieldCheck, Wrench, ArrowRight, Clock } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const highlights = [{
  title: "Fast Diagnostics & Repair",
  desc: "Pilot lights, thermocouples, gas control valves, and burner assemblies."
}, {
  title: "Energy-Efficient Replacements",
  desc: "Top-brand gas tank and high-recovery models sized for your household demand."
}, {
  title: "Electric to Gas Conversions",
  desc: "Full gas line extensions, proper flue venting, and safety valve setups."
}, {
  title: "Certified Safety Inspections",
  desc: "Carbon monoxide prevention, gas leak checks, and code-compliant installations."
}];
const badges = ["Licensed & Insured", "24/7 Urgent Dispatch", "Jonesboro & Clayton County", "Upfront Pricing"];
export default function WaterHeaterHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="gaswaterheaterhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Background Decorative Accents */}
      <div className="absolute inset-0 bg-radial-[at_20%_20%] from-secondary/15 via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-0 right-0 -mt-12 -mr-12 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 -mb-20 w-80 h-80 rounded-full bg-chart-3/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Content */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Top Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 text-xs sm:text-sm font-medium mb-6">
              <Flame className="size-4 text-accent" />
              <span>Emergency & Scheduled Gas Water Heater Specialists</span>
            </div>

            {/* Main Page H1 */}
            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-5xl/tight font-bold tracking-tight text-primary-foreground mb-6">
              Gas Water Heater Repair, Replacement &amp; Installation in Jonesboro, GA
            </h1>

            {/* Intro Copy */}
            <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed mb-8 max-w-2xl">
              Restore reliable hot water fast with certified local technicians. From emergency pilot light
              repairs and burner replacements to full energy-efficient gas installations and electric-to-gas conversions,
              we deliver safe, code-compliant plumbing solutions across Jonesboro and Clayton County.
            </p>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-bold text-base h-12 px-6">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="outline" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-transparent shadow-md font-semibold text-base h-12 px-6">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Quick Trust Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-primary-foreground/15 w-full">
              {badges.map((badge, i) => <div key={badge} data-index={i} className="flex items-center gap-2 text-xs text-primary-foreground/80">
                  <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                  <span>{badge}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column / Service Overview Card */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5 w-full">
            <div className="rounded-2xl bg-card text-card-foreground p-6 sm:p-8 shadow-2xl border border-border">
              <div className="flex items-center justify-between pb-5 border-b border-border mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                    <Wrench className="size-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold font-default text-card-foreground">
                      Complete Gas Water Heater Care
                    </h2>
                    <p className="text-xs text-muted-foreground">Prompt Clayton County Service</p>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-accent/15 text-accent-foreground font-semibold">
                  24/7 Available
                </Badge>
              </div>

              {/* Service Highlights List */}
              <div className="space-y-4 mb-6">
                {highlights.map((item, i) => <div key={item.title} data-index={i} className="p-3.5 rounded-lg bg-muted/60 border border-border/60 flex items-start gap-3">
                    <div className="p-1 rounded-md bg-accent text-accent-foreground mt-0.5 shrink-0">
                      <ShieldCheck className="size-3.5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-card-foreground">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>

              {/* Card Footer Info Banner */}
              <div className="rounded-lg bg-primary/5 p-4 border border-primary/15 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-card-foreground">
                  <Clock className="size-4 text-accent shrink-0" />
                  <span>No hot water? Emergency appointments dispatched right away.</span>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}